import { useState, useEffect } from 'react';
import { C, FONT, SectionHeader, fmtRelative, fmtDate, fmtSessionDate, getStageFromRecord, calendarDaysAgo } from './adminShared';
import { BmsFormModal } from './BmsFormModal';
import { EMAIL_PROBLEM, emailSendStatus, problemBadgeStyle } from './emailSendStatus';
import { DAYS, JOURNAL_PROMPTS_BY_DAY } from '../../utils/constants';
import { ClientCheckinsTab } from './ClientCheckinsTab';
import { ClientVideoTab } from './ClientVideoTab';
import {
  getAdminClients, getAdminClientDetail, getClientEmailHistory,
  inviteAdmin, listAdmins, removeAdmin, resendAdminInvite, setBmsOutcome,
} from '../../utils/api';

// Manual BMS outcome → the funnelStage it maps to when set (Stage 1 display layering).
// Option A: 'show_no_upsell_no_followup' reuses the SAME funnelStage as
// 'show_no_upsell' (bms_show_no_upsell), so the row Stage badge, "no upsell"
// filter chip, and all counts group it WITH show-no-upsell. Only its button +
// in-detail OUTCOME_LABELS text mark it as the no-follow-up variant, and the
// cron's exact-match select (bms_outcome='show_no_upsell') excludes it from Seq 8.
const OUTCOME_STAGE  = { show_upsell: 'bms_show_upsell', show_no_upsell: 'bms_show_no_upsell', show_no_upsell_no_followup: 'bms_show_no_upsell', no_show: 'bms_no_show' };
const OUTCOME_LABELS = { show_upsell: 'Show + upsell', show_no_upsell: 'Show + no upsell', show_no_upsell_no_followup: 'Show + no upsell — no follow-up', no_show: 'No-show' };

// Recompute a contact's post-BMS funnelStage for a given outcome. WHERE the
// outcome is set it maps via OUTCOME_STAGE; when cleared to null it reverts to
// the same call_attended/session_date derivation used in enrichWithDetail.
// Used to update the collapsed list row in place after an outcome change.
const stageForOutcome = (c, outcome) => {
  if (outcome && OUTCOME_STAGE[outcome]) return OUTCOME_STAGE[outcome];
  const sessionPast = c.sessionDate && new Date(c.sessionDate) < new Date();
  if (c.callAttended)                return 'bms_attended_no_upsell';
  if (c.callBooked && sessionPast)   return 'bms_noshow';
  if (c.callBooked)                  return 'bms_booked';
  return c.funnelStage; // pre-BMS clients (outcome control isn't shown for them anyway)
};
import { useAuth } from '../../hooks/useAuth';

// ─── Kept for backward-compat; populated from API at runtime ────────
export const ALL_CONTACTS = [];

// ─── Shape helpers ───────────────────────────────────────────────────
const mapListToContact = (u) => {
  const daysCompleted = u.daysCompleted || 0;
  // SAME PRECEDENCE AS enrichWithDetail. The list used to know only "booked or
  // not", so a contact whose session was two months ago still read bms_booked
  // here and only became bms_noshow once their row was expanded — list and
  // detail disagreed about the same person. /clients now returns callAttended
  // and sessionDate, so both derive the stage identically.
  const listSessionPast = u.sessionDate && new Date(u.sessionDate) < new Date();
  let funnelStage;
  if (u.callAttended)                    funnelStage = 'bms_attended_no_upsell';
  else if (u.callBooked && listSessionPast) funnelStage = 'bms_noshow';
  else if (u.callBooked)                 funnelStage = 'bms_booked';
  else if (daysCompleted >= 6) funnelStage = 'course_complete';
  else if (daysCompleted > 0)  funnelStage = 'course_active';
  else                         funnelStage = 'purchased';
  // Manual outcome (Stage 1) overrides the derived post-BMS stage WHERE set.
  if (u.bmsOutcome && OUTCOME_STAGE[u.bmsOutcome]) funnelStage = OUTCOME_STAGE[u.bmsOutcome];
  return {
    id: u.id, type: 'portal',
    email: u.email, firstName: u.firstName, lastName: u.lastName,
    optinDate: u.createdAt, purchasedAt: u.createdAt,
    lastActive: u.lastActive, daysCompleted,
    callBooked: u.callBooked || false,
    callAttended: u.callAttended || false,
    sessionDate: u.sessionDate ?? null,
    bmsOutcome: u.bmsOutcome ?? null,
    onboardingComplete: u.onboardingComplete,
    funnelStage, masterclassWatched: true,
    // Detail fields — populated when detail is fetched
    firstLogin: null, callAttended: false, sessionDate: null,
    uuuPurchased: false, bmsForm: null, journalEntries: [], emailEvents: [],
    progress: (u.progress || []).map(p => ({
      day: p.day_num, dayComplete: p.day_complete,
      videoComplete: p.video_complete, journalComplete: p.journal_complete,
      checkinComplete: p.checkin_complete, meditationComplete: p.meditation_complete,
    })),
  };
};

const enrichWithDetail = (base, detail, emails) => {
  const booking = detail.booking;
  const sessionPast = booking?.session_date && new Date(booking.session_date) < new Date();
  const bmsOutcome = booking?.bms_outcome ?? base.bmsOutcome ?? null;
  let funnelStage = base.funnelStage;
  if (booking?.call_attended)                       funnelStage = 'bms_attended_no_upsell';
  else if (booking?.call_booked && sessionPast)     funnelStage = 'bms_noshow';
  else if (booking?.call_booked)                    funnelStage = 'bms_booked';
  // Manual outcome (Stage 1) overrides the derived post-BMS stage WHERE set.
  if (bmsOutcome && OUTCOME_STAGE[bmsOutcome]) funnelStage = OUTCOME_STAGE[bmsOutcome];
  return {
    ...base,
    // first_login is written once, at the first ever login (backend
    // routes/auth.js). Until 2026-09-28 this read lastLogin, so the Overview's
    // "First login" line showed the LAST login.
    firstLogin:    detail.user?.firstLogin ?? null,
    // For the Progress tab's Onboarding row. lastLogin null = never logged in
    // (first_login and last_login are set together at login). There is no
    // onboarding timestamp on ry_users; the moment is recorded as Day 1's
    // unlocks_at, which POST /api/progress/onboarding/complete stamps in the
    // same request that sets onboarding_complete, and nothing else writes.
    lastLogin:     detail.user?.lastLogin ?? null,
    onboardedAt:   (detail.progress || []).find(p => p.day_num === 1)?.unlocks_at ?? null,
    callBooked:    booking?.call_booked   ?? base.callBooked,
    callAttended:  booking?.call_attended ?? false,
    sessionDate:   booking?.session_date  ?? null,
    bmsOutcome,
    bmsForm:       detail.bmsForm ?? null,
    journalEntries: detail.journalEntries ?? [],
    checkins: detail.checkins ?? [],
    funnelStage,
    progress: (detail.progress || []).map(p => ({
      day: p.day_num, dayComplete: p.day_complete,
      videoComplete: p.video_complete, journalComplete: p.journal_complete,
      checkinComplete: p.checkin_complete, meditationComplete: p.meditation_complete,
    })),
    emailEvents: (emails || []).map(e => ({
      seq: e.sequence, sentAt: e.created_at,
      openedAt: e.opened_at, clickedAt: e.clicked_at,
      sequenceLabel: e.sequenceLabel ?? null, subject: e.subject ?? null, trigger: e.trigger ?? null,
      // What actually happened to the send — see emailSendStatus.js.
      eventType: e.event_type ?? null, bouncedAt: e.bounced_at ?? null,
      resendMessageId: e.resend_message_id ?? null,
    })),
  };
};


// ─── THE FIVE BMS STATES ────────────────────────────────────────────────
// The filter row used to carry SIX BMS chips, and two pairs of them described
// the same person twice — 'BMS No-show ⚠' (system inferred it from the session
// date) sat beside 'BMS — No-show' (Ben confirmed it with a button), and
// 'BMS Attended — No Upsell' beside 'BMS — Show + no upsell'. Reading the row
// you could not tell which was which, and the two members of each pair are the
// SAME state at different levels of certainty.
//
// So certainty stopped being a tab and became a MARKER. Five states, each a
// place a person actually is; a ⚠ beside the name where the system inferred it
// and Ben has not confirmed it yet. That ⚠ is his to-do list: the calls still
// to be marked up.
//
// DERIVED, NOT STORED. Every state comes from call_booked / call_attended /
// session_date / bms_outcome, all of which already existed. No new tag, no new
// column, no new field, and none of the five outcome buttons writes anything
// different.
const BMS_STATES = [
  { id: 'bms_state_not_booked', label: 'Not booked'        },
  { id: 'bms_state_upcoming',   label: 'Booked — upcoming' },
  { id: 'bms_state_no_show',    label: 'Booked — no show'  },
  { id: 'bms_state_no_upsell',  label: 'Show — no upsell'  },
  { id: 'bms_state_upsell',     label: 'Show — upsell'     },
];
const BMS_STATE_IDS = new Set(BMS_STATES.map(s => s.id));

// A CONFIRMED outcome always wins over anything inferred — that is the whole
// point of Ben clicking the button. Only when there is no outcome do we fall
// back to what the booking implies.
const bmsStateOf = (c) => {
  const o = c.bmsOutcome;
  if (o === 'show_upsell')                    return 'bms_state_upsell';
  if (o === 'show_no_upsell' ||
      o === 'show_no_upsell_no_followup')     return 'bms_state_no_upsell';
  if (o === 'no_show')                        return 'bms_state_no_show';
  if (c.callAttended)                         return 'bms_state_no_upsell';   // inferred
  if (c.callBooked) {
    const past = c.sessionDate && new Date(c.sessionDate) < new Date();
    return past ? 'bms_state_no_show' : 'bms_state_upcoming';                 // inferred
  }
  return 'bms_state_not_booked';
};

// ⚠ = "the system put them here, you have not confirmed it".
// NOT on 'Not booked' — there is no call to mark up.
// NOT on 'Booked — upcoming' — the call has not happened yet, so there is
// nothing to mark up either, and a ⚠ there would nag about a future event.
// Structurally impossible on 'Show — upsell': nothing infers that state, it is
// only ever reached by Ben setting the outcome.
const bmsIsDerived = (c) => {
  const st = bmsStateOf(c);
  if (st === 'bms_state_not_booked' || st === 'bms_state_upcoming') return false;
  return !(c.bmsOutcome && OUTCOME_STAGE[c.bmsOutcome]);
};

// FU / NFU — meaningful ONLY inside 'Show — no upsell'.
// After a call Ben sometimes decides someone should not be chased: either the
// money genuinely is not there, or he would not want to work with them. Both
// are still "showed up, did not buy", so the state is right — but he needs to
// see at a glance who is not to be followed up.
//
// Read straight off the outcome he already picked. A contact with NO outcome
// (an older record, or one not marked up yet) shows NOTHING — guessing a
// default here would invent a decision he never made.
const bmsFollowUp = (c) => {
  if (bmsStateOf(c) !== 'bms_state_no_upsell') return null;
  if (c.bmsOutcome === 'show_no_upsell')                return 'FU';
  if (c.bmsOutcome === 'show_no_upsell_no_followup')    return 'NFU';
  return null;
};

const STAGE_FILTERS = [
  { id: 'all',                     label: 'All'                          },
  { id: 'lead',                    label: 'Lead'                         },
  { id: 'masterclass_watched',     label: 'Masterclass Watched'          },
  { id: 'purchased',               label: 'RY Purchased'                 },
  { id: 'course_active',           label: 'Course Active'                },
  { id: 'course_complete',         label: 'Course Complete'              },
  ...BMS_STATES,
  { id: 'uuu_client',              label: 'UUU Client'                   },
];

const fmtTime = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// BMS pre-call form values: week_landing and life_area_impacted are Postgres
// array columns — render them as comma-joined lists. Empty array → no value
// (so the field/block hides rather than showing an empty string).
const fmtFormVal = (v) => (Array.isArray(v) ? (v.length ? v.join(', ') : null) : v);

// ─── Sub-components ─────────────────────────────────────────────────
// Best-effort tidy label for an email_events.sequence key that has NO
// ry_sequence_emails match (e.g. "sequence_7a_reschedule"). Strips the
// "sequence_" prefix, upper-cases the sequence id's letter suffix (7a → 7A),
// drops the literal "email" token, and title-cases the rest. Never empty.
const tidySeqLabel = (rawKey) => {
  const base = String(rawKey || '').replace(/^sequence_/, '');
  if (!base) return rawKey || 'Email';
  const parts = base.split('_');
  const head = `Seq ${parts[0].replace(/([a-z]+)$/i, (m) => m.toUpperCase())}`;
  const rest = parts.slice(1)
    .filter(p => p && !/^email$/i.test(p))
    .map(p => p.charAt(0).toUpperCase() + p.slice(1));
  return rest.length ? `${head} — ${rest.join(' ')}` : head;
};

// Substitute the literal ${firstName} placeholder in a DB subject with the
// client's own first name (the emails themselves fall back to "there").
const renderSubject = (subject, firstName) =>
  String(subject).replace(/\$\{firstName\}/g, firstName || 'there');

// What happened to each send: emailSendStatus() / EMAIL_PROBLEM, emailSendStatus.js.

// Seq 1A (masterclass access) is recorded in email_events as 'sequence_1' —
// no stored key contains "1A", so the old includes('1A') test never matched.
const SEQ_1A_KEY = 'sequence_1';
// Never went out at all: the provider refused it, or it could not be confirmed.
const NOT_SENT = new Set(['failed', 'unknown']);
// "Went out" for an opened-rate: sent AND not rejected by the receiving server.
// A failed, unknown or bounced email could never have been opened.
const emailsThatWentOut = (events) =>
  (events || []).filter(e => !['failed', 'unknown', 'bounced'].includes(emailSendStatus(e)));

const EmailTimeline = ({ events, firstName }) => {
  if (!events?.length) return <div style={{ fontSize: 12, color: C.accent }}>No emails recorded yet</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {events.map((e, i) => {
        const label = e.sequenceLabel || tidySeqLabel(e.seq);
        const status = emailSendStatus(e);
        const problem = EMAIL_PROBLEM[status] || null;
        const neverSent = status === 'failed' || status === 'unknown';
        return (
          <div key={i} style={{ padding: '9px 0', borderBottom: i < events.length - 1 ? `1px solid ${C.border}` : 'none' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.04em', marginBottom: e.subject ? 2 : 4 }}>
              {label}
            </div>
            {e.subject && (
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: e.trigger ? 2 : 4 }}>
                {renderSubject(e.subject, firstName)}
              </div>
            )}
            {e.trigger && (
              <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', marginBottom: 4, lineHeight: 1.4 }}>
                {e.trigger}
              </div>
            )}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              {problem ? (
                <span style={problemBadgeStyle(problem)} title={problem.note}>
                  ⚠ {problem.label}: {fmtTime(e.sentAt) || '—'}
                </span>
              ) : (
                <span style={{ fontSize: 11, color: C.accent }}>Sent: {fmtTime(e.sentAt) || '—'}</span>
              )}
              {!neverSent && (
                <>
                  <span style={{ fontSize: 11, color: e.openedAt ? C.green : '#ccc' }}>
                    {e.openedAt ? `✓ Opened ${fmtTime(e.openedAt)}` : 'Not opened'}
                  </span>
                  <span style={{ fontSize: 11, color: e.clickedAt ? C.blue : '#ccc' }}>
                    {e.clickedAt ? `✓ Clicked ${fmtTime(e.clickedAt)}` : 'Not clicked'}
                  </span>
                </>
              )}
            </div>
            {problem && (
              <div style={{ fontSize: 10, color: problem.fg, marginTop: 3 }}>{problem.note}{neverSent ? ' (time shown is the attempt)' : ''}.</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TabBar = ({ tabs, active, onChange }) => (
  <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: `1px solid ${C.border}` }}>
    {tabs.map(t => (
      <button
        key={t.id}
        onClick={() => onChange(t.id)}
        style={{
          padding: '8px 16px',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: active === t.id ? 700 : 400,
          color: active === t.id ? C.heading : C.accent,
          borderBottom: `2px solid ${active === t.id ? C.heading : 'transparent'}`,
          marginBottom: -1,
          fontFamily: FONT,
          whiteSpace: 'nowrap',
        }}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const LeadDetail = ({ contact }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
      {[
        { label: 'Opted in',            done: true,                             date: contact.optinDate },
        { label: 'Seq 1A sent',         done: (contact.emailEvents || []).some(e => e.seq === SEQ_1A_KEY && !NOT_SENT.has(emailSendStatus(e))), date: null },
        { label: 'Masterclass watched', done: !!contact.masterclassWatched,    date: contact.masterclassWatchedAt },
        { label: 'RY landing visited',  done: !!contact.ryLandingVisited,      date: null },
        { label: 'Nurture active',      done: contact.nurtureStage > 0,        date: null },
      ].map((step, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, background: step.done ? C.greenBg : '#F5F5F5', border: `1px solid ${step.done ? '#A8D5C0' : C.border}` }}>
          <span style={{ fontSize: 12, color: step.done ? C.green : '#bbb' }}>{step.done ? '✓' : '○'}</span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: step.done ? C.green : C.accent }}>{step.label}</div>
            {step.date && <div style={{ fontSize: 10, color: C.accent }}>{fmtTime(step.date)}</div>}
          </div>
        </div>
      ))}
    </div>
    <div style={{ fontSize: 12, fontWeight: 700, color: C.heading, marginBottom: 6, marginTop: 4 }}>Email Timeline</div>
    <EmailTimeline events={contact.emailEvents} firstName={contact.firstName} />
  </div>
);

const PortalDetail = ({ contact, onOutcomeChange }) => {
  const [tab, setTab] = useState('overview');
  const [showBmsForm, setShowBmsForm] = useState(false); // read-only BMS form modal

  // ── Manual BMS outcome (Stage 1) — write-only; no email/automation reads it ──
  const [outcome, setOutcome]         = useState(contact.bmsOutcome ?? null);
  const [pending, setPending]         = useState(null); // { value, label, isReschedule } awaiting confirm, or null
  const [savingOutcome, setSaving]    = useState(false);
  const [outcomeError, setOutcomeErr] = useState('');

  const OUTCOME_BTNS = [
    { value: 'show_upsell',                label: 'Show + upsell' },
    { value: 'show_no_upsell',             label: 'Show + no upsell' },
    { value: 'show_no_upsell_no_followup', label: 'Show + no upsell — no follow-up' },
    { value: 'no_show',                    label: 'No-show' },
    { value: null,                         label: 'Reschedule', isReschedule: true },
  ];

  const confirmOutcome = async () => {
    if (!pending || savingOutcome) return;
    setSaving(true);
    setOutcomeErr('');
    try {
      const { data } = await setBmsOutcome(contact.id, pending.value);
      setOutcome(data?.bmsOutcome ?? null); // update local state so the in-detail badge reflects without reload
      // Bug 1 fix: also update the parent LIST row in place so the collapsed-row
      // badge + keySignal reflect the new outcome instantly (no hard refresh).
      onOutcomeChange?.(contact.id, data?.bmsOutcome ?? null, data?.bmsOutcomeSetAt ?? null);
      setPending(null);
    } catch (e) {
      setOutcomeErr(e.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview'  },
    { id: 'journal',  label: 'Journal'   },
    { id: 'checkins', label: 'Check-ins' },
    { id: 'video',    label: 'Video'     },
    { id: 'bms',      label: 'BMS Form'  },
    { id: 'progress', label: 'Progress'  },
    { id: 'emails',   label: 'Emails'    },
  ];

  const isBooked    = contact.callBooked;
  const sessionPast = contact.sessionDate && new Date(contact.sessionDate) < new Date();
  // bms_outcome is the source of truth WHERE set (uses local `outcome` state so it
  // reacts to a mark in this open detail): a 'show_*' outcome means they showed →
  // never the red no-show panel; 'no_show' → red panel is correct. NULL outcome →
  // keep today's call_attended/session_date derivation exactly (un-marked clients).
  const bmsStatus =
      (outcome === 'show_upsell' || outcome === 'show_no_upsell' || outcome === 'show_no_upsell_no_followup') ? 'attended'
    : outcome === 'no_show'      ? 'noshow'
    : contact.callAttended       ? 'attended'
    : (isBooked && sessionPast)  ? 'noshow'
    : isBooked                   ? 'upcoming'
    : 'not_booked';

  const BMS_COLORS = {
    upcoming:   { bg: C.blueBg,  border: '#B3D4F0', text: C.blue,  label: 'BMS Booked — upcoming'      },
    attended:   { bg: C.greenBg, border: '#A8D5C0', text: C.green, label: '✓ BMS Attended'              },
    noshow:     { bg: C.redBg,   border: '#F5C6C0', text: C.red,   label: '⚠ BMS No-show — follow up'  },
    not_booked: { bg: '#F5F5F5', border: C.border,  text: C.accent,label: 'BMS not booked'              },
  };
  const bms = BMS_COLORS[bmsStatus];

  // ── Call-day (day 7) display helpers — admin-only, self-contained ──
  // contentComplete: how many of days 1–6 are actually complete (0–6).
  // callDayWatched: whether the day-7 video-watch row exists (video_complete only).
  // displayCompleted: 0–7, counting the call day once the 6 content days are done.
  const contentComplete  = (contact.progress || []).filter(d => d.day >= 1 && d.day <= 6 && d.dayComplete === true).length;
  const callDayWatched   = (contact.progress || []).some(d => d.day === 7 && d.videoComplete === true);
  const bmsFormDone      = !!contact.bmsForm?.submitted_at; // Day-7 pre-call form submitted
  const displayCompleted = contentComplete + ((contentComplete >= 6 && callDayWatched) ? 1 : 0);

  // Journal entries keyed by day number
  const journalByDay = {};
  (contact.journalEntries || []).forEach(e => { journalByDay[e.day] = e; });

  return (
    <div>
      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {/* ── OVERVIEW (unchanged) ───────────────────────────────────── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 12 }}><span style={{ color: C.accent }}>Enrolled: </span>{fmtDate(contact.purchasedAt)}</div>
            <div style={{ fontSize: 12 }}><span style={{ color: C.accent }}>First login: </span>{fmtDate(contact.firstLogin)}</div>
            <div style={{ fontSize: 12 }}><span style={{ color: C.accent }}>Last active: </span>{fmtRelative(contact.lastActive)}</div>
            <div style={{ fontSize: 12 }}><span style={{ color: C.accent }}>Days completed: </span><strong>{displayCompleted}/7</strong></div>
          </div>
          <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ padding: '10px 14px', borderRadius: 8, background: bms.bg, border: `1px solid ${bms.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: bms.text, marginBottom: contact.sessionDate ? 4 : 0 }}>
                {bms.label}
              </div>
              {contact.sessionDate && (
                <div style={{ fontSize: 11, color: C.accent }}>{fmtSessionDate(contact.sessionDate)}</div>
              )}
            </div>
            {contact.uuuPurchased && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: C.heading }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#FFF8F7' }}>★ UUU Client</div>
              </div>
            )}

            {/* ── Manual BMS outcome (Stage 1) — write-only. Shown from bms_booked
                onward. Nothing downstream reads it yet, so setting it sends no email. ── */}
            {contact.callBooked && (
              <div style={{ padding: '12px 14px', borderRadius: 8, background: '#FDFAF8', border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>BMS Outcome</div>
                {outcome
                  ? <div style={{ fontSize: 12, fontWeight: 700, color: C.heading, marginBottom: 10 }}>Outcome: {OUTCOME_LABELS[outcome] || outcome}</div>
                  : <div style={{ fontSize: 12, color: C.accent, marginBottom: 10, fontStyle: 'italic' }}>No outcome set</div>
                }
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {OUTCOME_BTNS.map(btn => {
                    const active = outcome === btn.value && !btn.isReschedule;
                    return (
                      <button
                        key={btn.label}
                        onClick={() => { setOutcomeErr(''); setPending(btn); }}
                        disabled={savingOutcome}
                        style={{
                          padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                          fontFamily: FONT, cursor: savingOutcome ? 'default' : 'pointer',
                          border: `1px solid ${active ? C.heading : C.border}`,
                          background: active ? C.heading : C.card,
                          color: active ? '#FFF8F7' : C.text, whiteSpace: 'nowrap',
                        }}
                      >
                        {btn.label}
                      </button>
                    );
                  })}
                </div>
                {pending && (
                  <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: '#FFF8ED', border: '1px solid #F5D69A' }}>
                    <div style={{ fontSize: 12, color: C.text, marginBottom: 8 }}>
                      {pending.isReschedule
                        ? `Clear ${contact.firstName || 'this client'}'s outcome and keep them as booked?`
                        : `Mark ${contact.firstName || 'this client'} as "${pending.label}"?`}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={confirmOutcome} disabled={savingOutcome}
                        style={{ padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: FONT, border: 'none', background: savingOutcome ? C.border : C.heading, color: '#FFF8F7', cursor: savingOutcome ? 'default' : 'pointer' }}>
                        {savingOutcome ? 'Saving…' : 'Confirm'}
                      </button>
                      <button onClick={() => setPending(null)} disabled={savingOutcome}
                        style={{ padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: FONT, border: `1px solid ${C.border}`, background: C.card, color: C.accent, cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {outcomeError && <div style={{ marginTop: 8, fontSize: 11, color: C.red }}>✗ {outcomeError}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── JOURNAL — all 6 days, real prompt text, "Not yet submitted" ── */}
      {tab === 'journal' && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {DAYS.map((day, di) => {
            const entry = journalByDay[day.num];
            return (
              <div key={day.num} style={{ paddingBottom: 20, marginBottom: 20, borderBottom: di < DAYS.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.heading, marginBottom: 10 }}>
                  Day {day.num} — {day.title}
                </div>
                {entry ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {(JOURNAL_PROMPTS_BY_DAY[day.num] || []).map((prompt, i) => (
                      <div key={i} style={{ paddingLeft: 12, borderLeft: `3px solid ${C.border}` }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                          {prompt.label || `Prompt ${i + 1}`}
                        </div>
                        <div style={{ fontSize: 11, color: C.accent, fontStyle: 'italic', marginBottom: 5, lineHeight: 1.5 }}>
                          {prompt.q}
                        </div>
                        <div style={{ fontSize: 12, color: entry.answers[i] ? C.text : C.accent, lineHeight: 1.65, fontStyle: entry.answers[i] ? 'normal' : 'italic' }}>
                          {entry.answers[i] || 'Not answered'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: C.accent, fontStyle: 'italic' }}>Not yet submitted</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'checkins' && <ClientCheckinsTab checkins={contact.checkins} />}

      {tab === 'video' && <ClientVideoTab userId={contact.id} />}

      {/* ── BMS FORM — opens a read-only reproduction of the client's form ── */}
      {tab === 'bms' && (
        contact.bmsForm?.submitted_at ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 11, color: C.accent }}>
              Submitted {fmtDate(contact.bmsForm.submitted_at)} at{' '}
              {new Date(contact.bmsForm.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <button
              onClick={() => setShowBmsForm(true)}
              style={{ padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT, border: `1px solid ${C.heading}`, background: C.heading, color: '#FFF8F7', fontSize: 13, fontWeight: 700 }}
            >
              Open their BMS form
            </button>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: C.accent, fontStyle: 'italic' }}>
            {contact.callBooked
              ? 'Pre-call form not yet submitted.'
              : 'No BMS session booked — form not applicable.'}
          </div>
        )
      )}

      {/* ── PROGRESS — per-day completion status ─────────────────────── */}
      {tab === 'progress' && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {(() => {
            // ── ONBOARDING — the step before Day 1 ─────────────────────────
            // Three states, so "came in and did nothing" is told apart from
            // "actually onboarded". Display only. Date and time in the
            // viewer's own browser time zone, like every admin date.
            const known = 'lastLogin' in contact;   // set once detail has loaded
            const onboarded = !!contact.onboardingComplete;
            const hasDays = (contact.progress || []).some(d => d.day >= 1 && d.day <= 6);
            const when = contact.onboardedAt
              ? `${fmtDate(contact.onboardedAt)} ${new Date(contact.onboardedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
              : null;
            const status = onboarded
              ? <>✓ Onboarded{when ? <span style={{ fontWeight: 400, color: C.accent }}> · {when}</span> : null}</>
              : !known ? '—'
              : contact.lastLogin ? 'Logged in, not onboarded'
              : 'Not logged in yet';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: hasDays ? `1px solid ${C.border}` : 'none', flexWrap: 'wrap' }}>
                <div style={{ width: 76, flexShrink: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: onboarded ? C.green : C.accent }}>Onboarding</div>
                </div>
                <div style={{ flex: 1 }} />
                <div style={{ fontSize: 11, fontWeight: onboarded ? 700 : 400, color: onboarded ? C.green : C.accent, whiteSpace: 'nowrap' }}>
                  {status}
                </div>
              </div>
            );
          })()}
          {(contact.progress || []).length === 0 && (
            <div style={{ fontSize: 12, color: C.accent, fontStyle: 'italic' }}>No course access yet.</div>
          )}
          {(() => {
            // Render only days 1–6 with the normal mapper (Journal/Check-in/Meditation
            // pills don't apply to call day). The Call Day row is appended separately.
            const contentRows = (contact.progress || []).filter(d => d.day >= 1 && d.day <= 6);
            const showCallDay = contentComplete >= 6; // gated purely on the 6 content days being done
            return (
              <>
                {contentRows.map((d, i) => {
                  const dayInfo = DAYS.find(day => day.num === d.day);
                  const hasBorder = i < contentRows.length - 1 || showCallDay;
                  return (
                    <div key={d.day} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: hasBorder ? `1px solid ${C.border}` : 'none', flexWrap: 'wrap' }}>
                      <div style={{ width: 76, flexShrink: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: d.dayComplete ? C.green : C.accent }}>Day {d.day}</div>
                        <div style={{ fontSize: 10, color: C.accent, marginTop: 2 }}>{dayInfo?.title || ''}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                        {[
                          { label: 'Video',    done: d.videoComplete    },
                          { label: 'Journal',  done: d.journalComplete  },
                          { label: 'Check-in', done: d.checkinComplete  },
                          // Meditation badge only on days that have a guided meditation (3, 4, 5)
                          ...([3, 4, 5].includes(d.day) ? [{ label: 'Meditation', done: d.meditationComplete }] : []),
                        ].map(item => (
                          <span key={item.label} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: item.done ? C.greenBg : '#F5F5F5', color: item.done ? C.green : '#bbb', border: `1px solid ${item.done ? '#A8D5C0' : C.border}` }}>
                            {item.done ? '✓' : '○'} {item.label}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: d.dayComplete ? 700 : 400, color: d.dayComplete ? C.green : C.accent, whiteSpace: 'nowrap' }}>
                        {d.dayComplete ? '✓ Complete' : 'Not complete'}
                        {d.completedAt ? <span style={{ fontWeight: 400, color: C.accent }}> · {fmtDate(d.completedAt)}</span> : null}
                      </div>
                    </div>
                  );
                })}

                {/* Call Day (day 7) — shown once the 6 content days are done; green when the
                    call-day video is watched. Single Video pill (no Journal/Check-in/Meditation). */}
                {showCallDay && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: 'none', flexWrap: 'wrap' }}>
                    <div style={{ width: 76, flexShrink: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: callDayWatched ? C.green : C.accent }}>Day 7</div>
                      <div style={{ fontSize: 10, color: C.accent, marginTop: 2 }}>Call Day</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: callDayWatched ? C.greenBg : '#F5F5F5', color: callDayWatched ? C.green : '#bbb', border: `1px solid ${callDayWatched ? '#A8D5C0' : C.border}` }}>
                        {callDayWatched ? '✓' : '○'} Video
                      </span>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bmsFormDone ? C.greenBg : '#F5F5F5', color: bmsFormDone ? C.green : '#bbb', border: `1px solid ${bmsFormDone ? '#A8D5C0' : C.border}` }}>
                        {bmsFormDone ? '✓' : '✗'} Form
                      </span>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: callDayWatched ? 700 : 400, color: callDayWatched ? C.green : C.accent, whiteSpace: 'nowrap' }}>
                      {callDayWatched ? '✓ Complete' : 'Not complete'}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ── EMAILS ───────────────────────────────────────────────────── */}
      {tab === 'emails' && <EmailTimeline events={contact.emailEvents} firstName={contact.firstName} />}

      {showBmsForm && contact.bmsForm?.submitted_at && (
        <BmsFormModal contact={contact} onClose={() => setShowBmsForm(false)} />
      )}
    </div>
  );
};

// ─── Upcoming Sessions panel ─────────────────────────────────────────
const UpcomingSessions = ({ contacts }) => {
  const [expanded, setExpanded] = useState(null);

  const upcoming = contacts
    .filter(c => c.callBooked && c.sessionDate && new Date(c.sessionDate) > new Date())
    .sort((a, b) => new Date(a.sessionDate) - new Date(b.sessionDate));

  if (upcoming.length === 0) {
    return (
      <div style={{ padding: '32px 0', textAlign: 'center', color: C.accent, fontSize: 13 }}>
        No upcoming BMS sessions booked.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {upcoming.map((c) => {
        // Calendar days ahead on the VIEWER'S calendar: a session later today reads
        // "Today" (it used to round hours up, so "Today" could never appear).
        const daysAway = -calendarDaysAgo(c.sessionDate);
        const hasForm = !!c.bmsForm?.submitted_at;
        const isOpen = expanded === c.id;
        const f = c.bmsForm;

        // Small inline pillar score for the card header
        const PillarMini = ({ label, value }) => {
          const score = value ?? 0;
          const color = score >= 4 ? C.green : score >= 2 ? C.amber : C.red;
          return (
            <div style={{ textAlign: 'center', minWidth: 64 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>
                {score}<span style={{ fontSize: 10, color: C.accent, fontWeight: 400 }}>/5</span>
              </div>
              <div style={{ marginTop: 4, height: 3, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                <div style={{ width: `${(score / 5) * 100}%`, height: '100%', background: color, borderRadius: 2 }} />
              </div>
            </div>
          );
        };

        return (
          <div key={c.id} style={{
            background: C.card,
            border: `1.5px solid ${hasForm ? C.green : C.amber}40`,
            borderLeft: `4px solid ${hasForm ? C.green : C.amber}`,
            borderRadius: 10,
            overflow: 'hidden',
          }}>

            {/* ── Main row ── */}
            <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>

              {/* Name + date + countdown pill */}
              <div style={{ flex: '1 1 200px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.heading, marginBottom: 3 }}>
                  {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: C.accent }}>{c.email}</span>
                </div>
                <div style={{ fontSize: 12, color: hasForm ? C.green : C.accent, fontWeight: 600, marginBottom: 5 }}>
                  {fmtSessionDate(c.sessionDate)}
                </div>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                  background: daysAway <= 2 ? C.amberBg : C.greenBg,
                  color: daysAway <= 2 ? '#92650A' : C.green,
                  border: `1px solid ${daysAway <= 2 ? '#F5D69A' : '#A8D5C0'}`,
                }}>
                  {daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : `In ${daysAway} days`}
                </span>
              </div>

              {/* Right side: pillar scores + button (form submitted) OR warning tag (no form) */}
              {hasForm ? (
                <>
                  {/* Three pillar scores */}
                  <div style={{ display: 'flex', gap: 20, alignItems: 'center', flex: '0 0 auto' }}>
                    <PillarMini label="Inner Own."    value={f.inner_ownership_score} />
                    <PillarMini label="Emot. Legit."  value={f.emotional_legitimacy_score} />
                    <PillarMini label="Align. Action" value={f.aligned_action_score} />
                  </div>
                  {/* View Form toggle */}
                  <button
                    onClick={() => setExpanded(isOpen ? null : c.id)}
                    style={{ padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, border: `1px solid ${isOpen ? C.heading : C.border}`, background: isOpen ? C.heading : C.card, color: isOpen ? '#FFF8F7' : C.accent }}
                  >
                    {isOpen ? '▲ Close' : '▼ View Form'}
                  </button>
                </>
              ) : (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                  background: C.amberBg, color: '#92650A', border: `1px solid #F5D69A`,
                }}>
                  ⚠ Form not submitted
                </span>
              )}
            </div>{/* ── End main row ── */}

            {/* ── Expanded BMS form panel ── */}
            {isOpen && (
              <div style={{ borderTop: `1px solid ${C.border}`, padding: '16px 20px', background: '#FFF8F7', display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Zoom button */}
                {f.zoom_link && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <a href={f.zoom_link} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, background: '#2D8CFF', color: '#fff', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                      ▶ Join Zoom
                    </a>
                  </div>
                )}

                {/* All five scores */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Inner Ownership',      value: f.inner_ownership_score      },
                    { label: 'Emotional Legitimacy', value: f.emotional_legitimacy_score  },
                    { label: 'Aligned Action',        value: f.aligned_action_score       },
                    { label: 'Support',               value: f.support_score              },
                    { label: 'Readiness',             value: f.readiness_score            },
                  ].map(({ label, value }) => {
                    const score = value ?? 0;
                    const color = score >= 4 ? C.green : score >= 2 ? C.amber : C.red;
                    return (
                      <div key={label} style={{ padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, textAlign: 'center', minWidth: 90 }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>
                          {score}<span style={{ fontSize: 10, color: C.accent, fontWeight: 400 }}>/5</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Key text fields */}
                {[
                  { label: 'What landed this week', value: fmtFormVal(f.week_landing) },
                  { label: 'Biggest realisation',   value: f.biggest_realisation      },
                  { label: 'Main loop',              value: f.main_loop                },
                ].filter(x => x.value).map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>{value}</div>
                  </div>
                ))}

                {/* Core beliefs */}
                {[f.belief_1, f.belief_2, f.belief_3].some(Boolean) && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Core Beliefs</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {[f.belief_1, f.belief_2, f.belief_3].filter(Boolean).map((b, i) => (
                        <div key={i} style={{ padding: '7px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, color: C.text, fontStyle: 'italic' }}>"{b}"</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pattern duration + life area */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {f.pattern_duration && (
                    <div style={{ flex: 1, minWidth: 120, padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>Pattern Duration</div>
                      <div style={{ fontSize: 12, color: C.text }}>{f.pattern_duration}</div>
                    </div>
                  )}
                  {f.life_area_impacted && (
                    <div style={{ flex: 1, minWidth: 120, padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>Life Area</div>
                      <div style={{ fontSize: 12, color: C.text }}>{fmtFormVal(f.life_area_impacted)}</div>
                    </div>
                  )}
                </div>

                {f.successful_session && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Successful session looks like…</div>
                    <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>{f.successful_session}</div>
                  </div>
                )}

                {f.anything_else && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Anything else</div>
                    <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>{f.anything_else}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────
// ─── Owner-only: Invite a view-only admin (button + modal, self-contained) ───
function InviteAdminButton() {
  const [open, setOpen]           = useState(false);
  const [email, setEmail]         = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [status, setStatus]       = useState({ state: 'idle', message: null });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const sending    = status.state === 'sending';

  const reset = () => { setEmail(''); setFirstName(''); setLastName(''); setStatus({ state: 'idle', message: null }); };
  const close = () => { setOpen(false); reset(); };

  const submit = async () => {
    if (!emailValid) { setStatus({ state: 'error', message: 'Please enter a valid email address.' }); return; }
    setStatus({ state: 'sending', message: null });
    try {
      const { data } = await inviteAdmin({ email: email.trim(), first_name: firstName.trim(), last_name: lastName.trim() });
      if (data?.warning) {
        setStatus({ state: 'success', message: `Account created for ${data.email || email.trim()}, but the invite email couldn't be sent — you can resend it from the password-reset flow.` });
      } else {
        setStatus({ state: 'success', message: `Invite sent to ${data?.email || email.trim()}` });
      }
    } catch (err) {
      const resp = err.response;
      if (resp?.status === 409 && resp.data?.error === 'email_exists') {
        setStatus({ state: 'error', message: `This email already belongs to an RY account (${resp.data.name}) — it can't be invited as an admin.` });
      } else {
        setStatus({ state: 'error', message: "Couldn't send the invite — please try again." });
      }
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 13,
    border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: FONT,
    color: C.text, background: C.bg, outline: 'none', marginBottom: 12,
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
          border: `1px solid ${C.heading}`, background: C.heading, color: '#FFF8F7',
          fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        }}
      >
        + Invite admin
      </button>

      {open && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div style={{ background: C.card, borderRadius: 14, padding: '24px 26px', maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: `1px solid ${C.border}`, fontFamily: FONT }}>
            <h3 style={{ margin: '0 0 6px', fontFamily: "'Forum', serif", fontSize: '1.15rem', color: C.heading, letterSpacing: '0.04em' }}>
              Invite a view-only admin
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: C.accent, lineHeight: 1.6 }}>
              They'll get an email to set their own password. Invited admins can see everything but can't make changes.
            </p>

            {status.state === 'success' ? (
              <>
                <div style={{ background: C.greenBg || '#ECFBF3', border: '1px solid #A8D5C0', borderRadius: 8, padding: '12px 14px', color: C.green, fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
                  ✓ {status.message}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={close} style={{ padding: '9px 18px', background: C.heading, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.08em', marginBottom: 6 }}>EMAIL *</label>
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); if (status.state === 'error') setStatus({ state: 'idle', message: null }); }} placeholder="name@example.com" style={inputStyle} autoFocus disabled={sending} />

                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.08em', marginBottom: 6 }}>FIRST NAME</label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Optional" style={inputStyle} disabled={sending} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.08em', marginBottom: 6 }}>LAST NAME</label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Optional" style={inputStyle} disabled={sending} />
                  </div>
                </div>

                {status.state === 'error' && (
                  <div style={{ background: C.redBg, border: '1px solid #F5C6C0', borderRadius: 8, padding: '10px 12px', color: C.red, fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
                    {status.message}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                  <button onClick={close} disabled={sending} style={{ padding: '9px 16px', background: 'transparent', color: C.accent, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: sending ? 'not-allowed' : 'pointer', fontFamily: FONT }}>
                    Cancel
                  </button>
                  <button onClick={submit} disabled={sending || !emailValid} style={{ padding: '9px 18px', background: (sending || !emailValid) ? C.barTrack : C.heading, color: (sending || !emailValid) ? C.accent : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (sending || !emailValid) ? 'not-allowed' : 'pointer', fontFamily: FONT }}>
                    {sending ? 'Sending…' : 'Send invite'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Owner-only: Admins management card (list + remove + resend) ───
// Status comes from the backend: 'owner' | 'active' | 'pending'.
// Remove is a FLAG-FLIP on the server (is_admin=false, role=NULL). Never
// deletes the row, so an admin who happens to also be a paying client
// would keep their funnel/journal/booking data.
function AdminsCard() {
  const [admins, setAdmins]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [notice, setNotice]     = useState(null); // { kind:'ok'|'warn', text }
  const [rowBusy, setRowBusy]   = useState({});   // { [id]: 'remove' | 'resend' }
  const [confirmTarget, setConfirmTarget] = useState(null); // admin row pending remove
  const [removing, setRemoving] = useState(false);

  const load = async () => {
    try {
      const { data } = await listAdmins();
      setAdmins(Array.isArray(data?.admins) ? data.admins : []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  const flash = (kind, text) => setNotice({ kind, text });

  const displayName = (a) => {
    const n = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
    return n || a.email;
  };

  const formatLogin = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleResend = async (a) => {
    setRowBusy(b => ({ ...b, [a.id]: 'resend' }));
    try {
      const { data } = await resendAdminInvite(a.id);
      if (data?.warning) {
        flash('warn', `Token re-minted for ${data.email || a.email} but the email couldn't be sent.`);
      } else {
        flash('ok', `Invite re-sent to ${data?.email || a.email}.`);
      }
      await load();
    } catch (err) {
      flash('warn', err?.response?.data?.error || 'Resend failed.');
    } finally {
      setRowBusy(b => ({ ...b, [a.id]: undefined }));
    }
  };

  const handleConfirmRemove = async () => {
    if (!confirmTarget) return;
    setRemoving(true);
    try {
      await removeAdmin(confirmTarget.id);
      flash('ok', `${displayName(confirmTarget)} removed.`);
      await load();
    } catch (err) {
      flash('warn', err?.response?.data?.error || 'Remove failed.');
    } finally {
      setRemoving(false);
      setConfirmTarget(null);
    }
  };

  const statusBadge = (status) => {
    const map = {
      owner:   { label: 'Owner',                 fg: '#FFF8F7',  bg: C.heading },
      active:  { label: 'Active',                fg: C.green,    bg: C.greenBg, border: '#A8D5C0' },
      pending: { label: 'Pending — invite sent', fg: '#7A4A06',  bg: C.amberBg, border: '#E8C892' },
    };
    const m = map[status] || map.active;
    return (
      <span style={{
        display: 'inline-block', padding: '3px 9px', borderRadius: 999,
        fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
        color: m.fg, background: m.bg, border: m.border ? `1px solid ${m.border}` : 'none',
        fontFamily: FONT, whiteSpace: 'nowrap',
      }}>{m.label}</span>
    );
  };

  const secondaryBtn = {
    padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
    border: `1px solid ${C.border}`, background: C.card, color: C.accent,
    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
  };
  const dangerBtn = {
    padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
    border: '1px solid #F5C6C0', background: C.card, color: C.red,
    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
  };

  return (
    <div style={{
      background: C.card, borderRadius: 14, border: `1px solid ${C.border}`,
      padding: '20px 22px', marginBottom: 20, fontFamily: FONT, color: C.text,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, gap: 12, flexWrap: 'wrap',
      }}>
        <div>
          <h2 style={{
            margin: 0, fontFamily: "'Forum', serif", fontSize: '1.15rem',
            color: C.heading, letterSpacing: '0.04em',
          }}>
            Admins
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: C.accent, lineHeight: 1.5 }}>
            Owners have full access. Viewers can see the dashboards but can't make changes.
          </p>
        </div>
        <InviteAdminButton />
      </div>

      {/* Notice / error strips */}
      {notice && (
        <div style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12, lineHeight: 1.4,
          background: notice.kind === 'ok' ? C.greenBg : C.amberBg,
          color:      notice.kind === 'ok' ? C.green   : '#7A4A06',
          border:     notice.kind === 'ok' ? '1px solid #A8D5C0' : '1px solid #E8C892',
        }}>
          {notice.kind === 'ok' ? '✓ ' : '⚠ '}{notice.text}
        </div>
      )}
      {error && (
        <div style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12,
          background: C.redBg, color: C.red, border: '1px solid #F5C6C0',
        }}>
          ✗ {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ padding: '14px 4px', fontSize: 12, color: C.accent }}>Loading admins…</div>
      ) : admins.length === 0 ? (
        <div style={{ padding: '14px 4px', fontSize: 12, color: C.accent }}>No admins yet.</div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          {admins.map((a, i) => {
            const busy = rowBusy[a.id];
            const isOwner = a.status === 'owner';
            return (
              <div
                key={a.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '11px 14px',
                  borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                  background: C.card, flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                      {displayName(a)}
                    </span>
                    {statusBadge(a.status)}
                  </div>
                  <div style={{ fontSize: 11, color: C.accent, marginTop: 2, wordBreak: 'break-all' }}>
                    {a.email}
                  </div>
                  <div style={{ fontSize: 10, color: C.accent, marginTop: 2 }}>
                    Last sign-in: {a.status === 'pending' ? 'Never' : formatLogin(a.last_login)}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {!isOwner && a.status === 'pending' && (
                    <button
                      type="button"
                      onClick={() => handleResend(a)}
                      disabled={busy !== undefined}
                      style={{ ...secondaryBtn, opacity: busy ? 0.5 : 1, cursor: busy ? 'wait' : 'pointer' }}
                    >
                      {busy === 'resend' ? 'Sending…' : 'Resend invite'}
                    </button>
                  )}
                  {!isOwner && (
                    <button
                      type="button"
                      onClick={() => setConfirmTarget(a)}
                      disabled={busy !== undefined}
                      style={{ ...dangerBtn, opacity: busy ? 0.5 : 1, cursor: busy ? 'wait' : 'pointer' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Remove confirmation modal */}
      {confirmTarget && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={(e) => { if (e.target === e.currentTarget && !removing) setConfirmTarget(null); }}
        >
          <div style={{
            background: C.card, borderRadius: 14, padding: '24px 26px', maxWidth: 440, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: `1px solid ${C.border}`, fontFamily: FONT,
          }}>
            <h3 style={{
              margin: '0 0 6px', fontFamily: "'Forum', serif", fontSize: '1.15rem',
              color: C.heading, letterSpacing: '0.04em',
            }}>
              Remove admin
            </h3>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: C.text, lineHeight: 1.5 }}>
              Remove <strong>{displayName(confirmTarget)}</strong> ({confirmTarget.email})?
            </p>
            <div style={{
              background: C.amberBg, border: '1px solid #E8C892', borderRadius: 8,
              padding: '10px 12px', fontSize: 12, color: '#7A4A06', lineHeight: 1.5, marginBottom: 18,
            }}>
              This removes their admin access. If they're currently signed in, their
              session may stay active for up to 30 days — ask them to log out for an
              immediate cut-off.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirmTarget(null)}
                disabled={removing}
                style={{
                  padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
                  border: `1px solid ${C.border}`, background: C.card, color: C.accent,
                  fontSize: 13, fontWeight: 700,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemove}
                disabled={removing}
                style={{
                  padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
                  border: '1px solid #C0392B', background: C.red, color: '#fff',
                  fontSize: 13, fontWeight: 700,
                }}
              >
                {removing ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const AdminUsers = () => {
  const { user } = useAuth();
  const [search, setSearch]               = useState('');
  const [stageFilter, setStageFilter]     = useState('all');
  const [expanded, setExpanded]           = useState(null);
  const [sortKey, setSortKey]             = useState('optinDate');
  const [sortDir, setSortDir]             = useState(-1);
  const [view, setView]                   = useState('all'); // 'all' | 'sessions'
  const [contacts, setContacts]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [loadedDetails, setLoadedDetails] = useState(new Set());
  const [loadingDetail, setLoadingDetail] = useState({});

  // ── Initial load: fetch client list + pre-fetch detail for booked users ──
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getAdminClients();
        const list = (res.data || []).map(mapListToContact);
        if (!active) return;
        setContacts(list);

        // Pre-fetch detail for callBooked users so UpcomingSessions has session dates
        const bookedUsers = list.filter(c => c.callBooked);
        if (bookedUsers.length > 0) {
          const results = await Promise.allSettled(
            bookedUsers.map(c =>
              Promise.all([getAdminClientDetail(c.id), getClientEmailHistory(c.id)])
                .then(([dRes, eRes]) => ({ id: c.id, base: c, detail: dRes.data, emails: eRes.data }))
            )
          );
          if (!active) return;
          const enrichedById = {};
          const loadedSet = new Set();
          results.forEach(r => {
            if (r.status === 'fulfilled' && r.value) {
              const { id, base, detail, emails } = r.value;
              enrichedById[id] = enrichWithDetail(base, detail, emails);
              loadedSet.add(id);
            }
          });
          setContacts(prev => prev.map(c => enrichedById[c.id] ?? c));
          setLoadedDetails(loadedSet);
        }
      } catch (err) {
        if (active) setError(err.response?.data?.error || err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // ── Expand a row: lazy-load detail + email history if not yet loaded ──
  const handleExpand = async (contactId) => {
    if (expanded === contactId) { setExpanded(null); return; }
    setExpanded(contactId);
    if (!loadedDetails.has(contactId)) {
      setLoadingDetail(prev => ({ ...prev, [contactId]: true }));
      try {
        const base = contacts.find(c => c.id === contactId);
        if (base) {
          const [dRes, eRes] = await Promise.all([
            getAdminClientDetail(contactId),
            getClientEmailHistory(contactId),
          ]);
          const enriched = enrichWithDetail(base, dRes.data, eRes.data);
          setContacts(prev => prev.map(c => c.id === contactId ? enriched : c));
          setLoadedDetails(prev => new Set([...prev, contactId]));
        }
      } catch (err) {
        console.error('Failed to load detail for', contactId, err);
      } finally {
        setLoadingDetail(prev => ({ ...prev, [contactId]: false }));
      }
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  // Bug 1 fix: after PortalDetail writes a BMS outcome, patch the matching list
  // row in place (outcome fields + recomputed funnelStage) so the collapsed row's
  // badge + keySignal update instantly — no full refetch/refresh.
  const handleOutcomeChange = (userId, newOutcome, newSetAt) => {
    setContacts(prev => prev.map(c =>
      c.id === userId
        ? { ...c, bmsOutcome: newOutcome, bmsOutcomeSetAt: newSetAt, funnelStage: stageForOutcome(c, newOutcome) }
        : c
    ));
  };

  const filtered = (() => {
    let list = contacts;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.email?.toLowerCase().includes(q) ||
        c.firstName?.toLowerCase().includes(q) ||
        c.lastName?.toLowerCase().includes(q)
      );
    }
    // BMS chips match the MERGED state; every other chip still matches
    // funnelStage exactly as before.
    if (stageFilter !== 'all') {
      list = BMS_STATE_IDS.has(stageFilter)
        ? list.filter(c => bmsStateOf(c) === stageFilter)
        : list.filter(c => c.funnelStage === stageFilter);
    }
    return [...list].sort((a, b) => {
      let av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
      if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      if (av < bv) return -sortDir; if (av > bv) return sortDir; return 0;
    });
  })();

  // Counted the same two ways the filter matches, so a chip's badge always
  // equals the number of rows clicking it produces.
  const stageCounts = contacts.reduce((acc, c) => {
    acc[c.funnelStage] = (acc[c.funnelStage] || 0) + 1;
    const st = bmsStateOf(c);
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});
  const upcomingCount = contacts.filter(c => c.callBooked && c.sessionDate && new Date(c.sessionDate) > new Date()).length;

  const SortTh = ({ col, label }) => (
    <th onClick={() => handleSort(col)} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: sortKey === col ? C.heading : C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', textAlign: 'left', userSelect: 'none', whiteSpace: 'nowrap', borderBottom: `2px solid ${C.border}`, background: '#FDFAF8' }}>
      {label} {sortKey === col ? (sortDir === 1 ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>

      {/* Error banner */}
      {error && (
        <div style={{ background: C.redBg, border: '1px solid #F5C6C0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: C.red }}>
          ✗ Failed to load contacts: {error}
        </div>
      )}

      {/* Owner-only: Admins management (list + remove + resend). InviteAdminButton lives inside this card's header. */}
      {user?.role === 'owner' && <AdminsCard />}

      {/* View toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <SectionHeader
          title={view === 'sessions' ? 'Upcoming BMS Sessions' : 'All Contacts'}
          sub={view === 'sessions'
            ? `${upcomingCount} session${upcomingCount !== 1 ? 's' : ''} coming up`
            : loading ? 'Loading…' : `${contacts.length} people in your world — from opt-in to UUU client`}
        />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {[
            { id: 'all',      label: 'All Contacts' },
            { id: 'sessions', label: `Upcoming Sessions${upcomingCount > 0 ? ` (${upcomingCount})` : ''}` },
          ].map(v => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              style={{
                padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
                border: `1px solid ${view === v.id ? C.heading : C.border}`,
                background: view === v.id ? C.heading : C.card,
                color: view === v.id ? '#FFF8F7' : C.accent,
                fontSize: 12, fontWeight: 600,
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'sessions' ? (
        <UpcomingSessions contacts={contacts} />
      ) : (
        <>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name or email…"
            style={{ width: '100%', padding: '9px 14px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: FONT, color: C.text, background: C.card, outline: 'none', marginBottom: 14, boxSizing: 'border-box' }}
          />

          {/* Stage filter pills */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
            {STAGE_FILTERS.map(f => {
              const count = f.id === 'all' ? contacts.length : (stageCounts[f.id] || 0);
              const active = stageFilter === f.id;
              const isNoShow = f.id === 'bms_state_no_show';
              const isPurple = f.id === 'bms_state_no_upsell';
              return (
                <button key={f.id} onClick={() => setStageFilter(f.id)} style={{
                  padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontFamily: FONT,
                  border: `1px solid ${active ? (isNoShow ? C.red : isPurple ? '#5A2D82' : C.heading) : C.border}`,
                  background: active ? (isNoShow ? C.red : isPurple ? '#5A2D82' : C.heading) : C.card,
                  color: active ? '#FFF8F7' : (isNoShow ? C.red : isPurple ? '#5A2D82' : C.accent),
                  fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {f.label}
                  {count > 0 && (
                    <span style={{ background: active ? 'rgba(255,255,255,0.25)' : C.border, color: active ? '#fff' : C.accent, borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            <div style={{ fontSize: 12, color: C.accent, display: 'flex', alignItems: 'center', marginLeft: 4 }}>
              {filtered.length} shown
            </div>
          </div>

          {/* Table */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: C.accent, fontSize: 13 }}>Loading contacts…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: C.accent, fontSize: 13 }}>No contacts match this filter</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <SortTh col="firstName" label="Name" />
                      <SortTh col="email"     label="Email" />
                      <SortTh col="funnelStage" label="Stage" />
                      <SortTh col="optinDate"  label="Opted In" />
                      <SortTh col="lastActive" label="Last Active" />
                      <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `2px solid ${C.border}`, background: '#FDFAF8' }}>Key Signal</th>
                      <th style={{ padding: '10px 14px', borderBottom: `2px solid ${C.border}`, background: '#FDFAF8', width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => {
                      const stage = getStageFromRecord(c);
                      const isOpen = expanded === c.id;
                      const bmsState  = bmsStateOf(c);
                      const isNoShow  = bmsState === 'bms_state_no_show';
                      const isAttendedNoUpsell = bmsState === 'bms_state_no_upsell';
                      // ⚠ = inferred, not yet confirmed. FU/NFU = the follow-up
                      // decision Ben already made. Both read off existing data.
                      const needsMarkup = bmsIsDerived(c);
                      const followUp    = bmsFollowUp(c);

                      const keySignal = (() => {
                        if (c.funnelStage === 'lead')                    { const out = emailsThatWentOut(c.emailEvents); return `${out.filter(e => e.openedAt).length}/${out.length} emails opened`; }
                        if (c.funnelStage === 'masterclass_watched')      return `Watched ${fmtDate(c.masterclassWatchedAt)} · Nurture email ${c.nurtureStage}/3`;
                        if (c.funnelStage === 'purchased')                return 'Purchased — not started course';
                        if (c.funnelStage === 'course_active')            return `Day ${c.daysCompleted}/7 in progress`;
                        if (c.funnelStage === 'course_complete')          return 'Day 6 done — BMS not booked';
                        if (c.funnelStage === 'bms_booked')               return `Session: ${fmtSessionDate(c.sessionDate)}`;
                        if (c.funnelStage === 'bms_noshow')               return `No-show · ${fmtSessionDate(c.sessionDate)}`;
                        if (c.funnelStage === 'bms_attended_no_upsell')   return `Attended ${fmtDate(c.sessionDate)} · Seq 8 running — no UUU yet`;
                        // Manual-outcome labels (Stage 1) — accurate to the set outcome; no Seq 8 assumption.
                        if (c.funnelStage === 'bms_show_upsell')          return `BMS Attended — Purchased UUU`;
                        if (c.funnelStage === 'bms_show_no_upsell')       return `BMS Attended — No purchase`;
                        if (c.funnelStage === 'bms_no_show')              return `BMS No-show`;
                        if (c.funnelStage === 'uuu_client')               return 'UUU purchased ★';
                        return '—';
                      })();

                      return (
                        <>
                          <tr
                            key={c.id}
                            onClick={() => handleExpand(c.id)}
                            style={{ borderBottom: isOpen ? 'none' : `1px solid ${C.border}`, background: isNoShow ? '#FFF5F5' : isAttendedNoUpsell ? '#FAF5FF' : isOpen ? '#FFF8F7' : i % 2 === 0 ? C.card : '#FDFAF8', cursor: 'pointer' }}
                          >
                            <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: isNoShow ? C.red : isAttendedNoUpsell ? '#5A2D82' : C.text, whiteSpace: 'nowrap' }}>
                              {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                              {c.gender && <span style={{ marginLeft: 6, fontSize: 10, background: '#F0F0F0', color: '#666', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>{c.gender}</span>}
                              {isNoShow && <span style={{ marginLeft: 6, fontSize: 10, background: C.redBg, color: C.red, padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>NO-SHOW</span>}
                              {/* Ben's to-do list: the system put them in this
                                  state, he has not confirmed it with a button. */}
                              {needsMarkup && (
                                <span title="Inferred from the session date — outcome not marked up yet"
                                      style={{ marginLeft: 6, fontSize: 10, color: C.amber, fontWeight: 700 }}>⚠</span>
                              )}
                              {/* Replaces the old blanket "FOLLOW UP" badge,
                                  which said the same thing about everyone who
                                  attended regardless of what Ben decided. */}
                              {followUp && (
                                <span title={followUp === 'FU' ? 'Follow-up sequence running' : 'No follow-up — do not chase'}
                                      style={{ marginLeft: 6, fontSize: 10,
                                               background: followUp === 'FU' ? '#F3EDF9' : C.redBg,
                                               color: followUp === 'FU' ? '#5A2D82' : C.red,
                                               padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>{followUp}</span>
                              )}
                            </td>
                            <td style={{ padding: '12px 14px', fontSize: 12, color: C.accent, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</td>
                            <td style={{ padding: '12px 14px' }}>
                              <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: stage.color, background: stage.bg, whiteSpace: 'nowrap' }}>
                                {stage.label}
                              </span>
                              {c.funnelStage === 'bms_booked' && c.sessionDate && (
                                <div style={{ fontSize: 10, color: C.green, marginTop: 4, whiteSpace: 'nowrap', fontWeight: 600 }}>
                                  {fmtSessionDate(c.sessionDate)}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '12px 14px', fontSize: 12, color: C.accent, whiteSpace: 'nowrap' }}>{fmtDate(c.optinDate)}</td>
                            <td style={{ padding: '12px 14px', fontSize: 12, color: C.accent, whiteSpace: 'nowrap' }}>{fmtRelative(c.lastActive)}</td>
                            <td style={{ padding: '12px 14px', fontSize: 12, color: isNoShow ? C.red : C.accent }}>{keySignal}</td>
                            <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: 12, color: C.accent }}>{isOpen ? '▲' : '▼'}</td>
                          </tr>

                          {isOpen && (
                            <tr key={`${c.id}-exp`} style={{ borderBottom: `1px solid ${C.border}` }}>
                              <td colSpan={7} style={{ padding: '20px 22px', background: '#FFF8F7' }}>
                                {loadingDetail[c.id]
                                  ? <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 13, color: C.accent }}>Loading details…</div>
                                  : c.type === 'lead' ? <LeadDetail contact={c} /> : <PortalDetail contact={c} onOutcomeChange={handleOutcomeChange} />
                                }
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
